import type { User, CreateUserRequest, LoginRequest, AuthResponse } from '@trading-app/shared';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

export class AuthService {
  private static instance: AuthService | null = null;
  private token: string | null = null;
  private refreshToken: string | null = null;
  private user: User | null = null;
  private refreshTimer: NodeJS.Timeout | null = null;

  private constructor() {
    // Load tokens from localStorage on initialization
    this.token = localStorage.getItem('auth_token');
    this.refreshToken = localStorage.getItem('refresh_token');
  }

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  /**
   * Get current auth token
   */
  getToken(): string | null {
    return this.token;
  }

  /**
   * Get current user
   */
  getUser(): User | null {
    return this.user;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.token && !!this.user;
  }

  /**
   * Set authentication tokens
   */
  private setTokens(token: string, refreshToken?: string): void {
    this.token = token;
    localStorage.setItem('auth_token', token);
    
    if (refreshToken) {
      this.refreshToken = refreshToken;
      localStorage.setItem('refresh_token', refreshToken);
    }
    
    // Schedule automatic refresh before token expires (refresh at 80% of lifetime)
    this.scheduleTokenRefresh();
  }

  /**
   * Clear authentication
   */
  private clearAuth(): void {
    this.token = null;
    this.refreshToken = null;
    this.user = null;
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    
    // Clear refresh timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Schedule automatic token refresh
   */
  private scheduleTokenRefresh(): void {
    // Clear existing timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    // Parse token to get expiration
    if (!this.token) return;

    try {
      const payload = JSON.parse(atob(this.token.split('.')[1]));
      const expiresIn = (payload.exp * 1000) - Date.now();

      // Refresh at 80% of token lifetime (12 minutes for 15-minute tokens)
      const refreshIn = Math.max(expiresIn * 0.8, 30000); // At least 30 seconds

      this.refreshTimer = setTimeout(() => {
        this.refreshAccessToken();
      }, refreshIn);
    } catch (error) {
      console.error('Failed to schedule token refresh:', error);
    }
  }

  /**
   * Refresh access token using refresh token
   */
  async refreshAccessToken(): Promise<boolean> {
    try {
      if (!this.refreshToken) {
        return false;
      }

      const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken: this.refreshToken }),
      });

      if (response.ok) {
        const result = await response.json();
        this.setTokens(result.token, result.refreshToken);
        return true;
      } else {
        // Refresh failed, clear auth
        this.clearAuth();
        return false;
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      return false;
    }
  }

  /**
   * Make authenticated API request with automatic retry on 401
   */
  private async authenticatedFetch(url: string, options: RequestInit = {}): Promise<Response> {
    const makeRequest = async () => {
      const headers = new Headers(options.headers);
      
      if (this.token) {
        headers.set('Authorization', `Bearer ${this.token}`);
      }
      
      return fetch(url, {
        ...options,
        headers,
      });
    };

    let response = await makeRequest();
    
    // If we get a 401, try refreshing the token and retry once
    if (response.status === 401 && this.refreshToken) {
      const refreshed = await this.refreshAccessToken();
      if (refreshed) {
        response = await makeRequest();
      }
    }
    
    return response;
  }

  /**
   * Register a new user
   */
  async register(data: CreateUserRequest): Promise<AuthResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        this.setTokens(result.token, result.refreshToken);
        this.user = result.user;
        
        return {
          success: true,
          user: result.user,
          token: result.token,
        };
      } else {
        return {
          success: false,
          message: result.message || result.error || 'Registration failed',
        };
      }
    } catch (error) {
      console.error('Registration error:', error);
      return {
        success: false,
        message: 'Network error during registration',
      };
    }
  }

  /**
   * Login user
   */
  async login(data: LoginRequest): Promise<AuthResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        this.setTokens(result.token, result.refreshToken);
        this.user = result.user;
        
        return {
          success: true,
          user: result.user,
          token: result.token,
        };
      } else {
        return {
          success: false,
          message: result.message || result.error || 'Login failed',
        };
      }
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        message: 'Network error during login',
      };
    }
  }

  /**
   * Logout user
   */
  logout(): void {
    this.clearAuth();
  }

  /**
   * Get current user profile from server
   */
  async getCurrentUser(): Promise<User | null> {
    try {
      if (!this.token) {
        return null;
      }

      const response = await this.authenticatedFetch(`${API_BASE_URL}/api/auth/me`);
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          this.user = result.user;
          return result.user;
        }
      } else if (response.status === 401) {
        // Token is invalid, clear auth
        this.clearAuth();
      }

      return null;
    } catch (error) {
      console.error('Get current user error:', error);
      return null;
    }
  }

  /**
   * Initialize auth state (call on app startup)
   */
  async initialize(): Promise<boolean> {
    console.log('AuthService.initialize() called');
    console.log('Token exists:', !!this.token);
    console.log('Refresh token exists:', !!this.refreshToken);
    
    if (!this.token) {
      console.log('No token found, returning false');
      return false;
    }

    // Try to get current user
    console.log('Attempting to get current user...');
    const user = await this.getCurrentUser();
    console.log('Current user result:', user);
    
    if (user) {
      // Schedule token refresh if we have a valid session
      console.log('User found, scheduling token refresh');
      this.scheduleTokenRefresh();
      return true;
    } else if (this.refreshToken) {
      // Try to refresh the token if getCurrentUser failed
      console.log('No user found, attempting token refresh...');
      const refreshed = await this.refreshAccessToken();
      console.log('Token refresh result:', refreshed);
      if (refreshed) {
        // Try getting user again after refresh
        console.log('Token refreshed, getting user again...');
        const userAfterRefresh = await this.getCurrentUser();
        console.log('User after refresh:', userAfterRefresh);
        return !!userAfterRefresh;
      }
    }
    
    console.log('Auth initialization failed');
    return false;
  }
}

// Export singleton instance
export const authService = AuthService.getInstance();